/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { dashboardsAddWidget } from "../funcs/dashboardsAddWidget.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { Dashboard } from "../models/components/dashboard.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  AddDashboardWidgetRequest,
  AddDashboardWidgetSecurity,
} from "../models/operations/adddashboardwidget.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type AddDashboardWidgetMutationVariables = {
  request: AddDashboardWidgetRequest;
  security?: AddDashboardWidgetSecurity | undefined;
  options?: RequestOptions;
};

export type AddDashboardWidgetMutationData = Dashboard;

export type AddDashboardWidgetMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * addDashboardWidget dashboards
 *
 * @remarks
 * Place a saved widget on a dashboard, as a new card at the bottom, sized for its chart type.
 */
export function useAddDashboardWidgetMutation(
  options?: MutationHookOptions<
    AddDashboardWidgetMutationData,
    AddDashboardWidgetMutationError,
    AddDashboardWidgetMutationVariables
  >,
): UseMutationResult<
  AddDashboardWidgetMutationData,
  AddDashboardWidgetMutationError,
  AddDashboardWidgetMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildAddDashboardWidgetMutation(client, options),
    ...options,
  });
}

export function mutationKeyAddDashboardWidget(): MutationKey {
  return ["@gram/client", "dashboards", "addWidget"];
}

export function buildAddDashboardWidgetMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: AddDashboardWidgetMutationVariables,
  ) => Promise<AddDashboardWidgetMutationData>;
} {
  return {
    mutationKey: mutationKeyAddDashboardWidget(),
    mutationFn: function addDashboardWidgetMutationFn({
      request,
      security,
      options,
    }): Promise<AddDashboardWidgetMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(dashboardsAddWidget(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
